// =============================================================================
// BrightPath - Toast Hook
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// 
// NOTE: This file should already exist in your project from UI component setup.
// This is included as a reference/stub in case it's missing.
// =============================================================================

import { useCallback } from 'react';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'destructive' | 'warning';
  duration?: number;
}

interface ToastAPI {
  toast: (options: ToastOptions) => void;
  dismiss: (id?: string) => void;
}

/**
 * Hook for displaying toast notifications
 * 
 * This is a stub implementation. In production, this would connect
 * to your toast provider (e.g., react-hot-toast, sonner, or custom).
 */
export function useToast(): ToastAPI {
  const toast = useCallback((options: ToastOptions) => {
    // In production, this would trigger a toast notification
    // For now, we'll just log to console
    const { title, description, variant = 'default' } = options;
    
    const prefix = variant === 'destructive' 
      ? '❌' 
      : variant === 'success' 
      ? '✅' 
      : variant === 'warning'
      ? '⚠️'
      : 'ℹ️';
    
    console.log(`${prefix} [Toast] ${title}${description ? `: ${description}` : ''}`);
    
    // If you're using a toast library, dispatch here:
    // Example with sonner:
    // import { toast as sonnerToast } from 'sonner';
    // sonnerToast[variant === 'destructive' ? 'error' : variant](title, { description });
  }, []);
  
  const dismiss = useCallback((_id?: string) => {
    // Dismiss a specific toast or all toasts
    console.log('[Toast] Dismissed');
  }, []);
  
  return { toast, dismiss };
}

export default useToast;
